/* ============================================================
   Happy Toilet — theme.js
   Vanilla JS, no framework dependencies.
   ============================================================ */

'use strict';

/* ── Utilities ─────────────────────────────────────────────── */
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

function debounce(fn, wait) {
  let timer;
  return (...args) => { clearTimeout(timer); timer = setTimeout(() => fn(...args), wait); };
}

/* ── Cart State ─────────────────────────────────────────────── */
const Cart = {
  async get() {
    const res = await fetch('/cart.js');
    return res.json();
  },

  async add(items) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.description || 'Could not add to cart');
    }
    return res.json();
  },

  async change(id, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id, quantity }),
    });
    return res.json();
  },

  async remove(id) {
    return Cart.change(id, 0);
  },
};

/* ── Cart Count Badge ───────────────────────────────────────── */
function updateCartCount(count) {
  $$('#CartCount').forEach(el => {
    el.textContent = count;
    el.classList.toggle('cart-count--empty', count === 0);
    el.setAttribute('aria-hidden', count === 0 ? 'true' : 'false');
  });
}

/* ── Cart Drawer ────────────────────────────────────────────── */
const CartDrawer = {
  el: null,

  init() {
    this.el = $('#CartDrawer');
    if (!this.el) return;

    // Open triggers
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-toggle]')) {
        e.preventDefault();
        this.open();
      }
      if (e.target.closest('[data-cart-drawer-close]') || e.target.closest('[data-cart-drawer-overlay]')) {
        this.close();
      }
    });

    // Keyboard close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) this.close();
    });

    // Qty changes inside drawer
    this.el.addEventListener('click', (e) => {
      const decreaseBtn = e.target.closest('[data-cart-decrease]');
      const increaseBtn = e.target.closest('[data-cart-increase]');
      const removeBtn = e.target.closest('[data-cart-remove]');

      if (decreaseBtn) this.updateItem(decreaseBtn.dataset.cartDecrease, -1);
      if (increaseBtn) this.updateItem(increaseBtn.dataset.cartIncrease, 1);
      if (removeBtn) this.removeItem(removeBtn.dataset.cartRemove);
    });
  },

  isOpen() {
    return this.el && this.el.getAttribute('aria-hidden') === 'false';
  },

  open() {
    if (!this.el) return;
    this.el.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this.el.querySelector('[data-cart-drawer-close]')?.focus();
    // Refresh discount panel whenever the drawer is opened so it never shows
    // stale state (e.g. after navigating from checkout back to /cart).
    document.dispatchEvent(new CustomEvent('cart:drawer-opened'));
  },

  close() {
    if (!this.el) return;
    this.el.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  },

  async updateItem(key, delta) {
    const qtyEl = this.el.querySelector(`[data-item-qty="${key}"]`);
    if (!qtyEl) return;
    const current = parseInt(qtyEl.textContent, 10) || 1;
    const newQty = Math.max(0, current + delta);
    await Cart.change(key, newQty);
    await this.refresh();
  },

  async removeItem(key) {
    await Cart.remove(key);
    await this.refresh();
  },

  async refresh() {
    // Use Shopify's Section Rendering API so Liquid handles image URLs,
    // selling plan names, and compare prices — no JS string-building needed.
    const sectionData = await fetch('/?sections=cart-items').then(r => r.json());
    const rawHtml = sectionData['cart-items'] || '';
    const doc = new DOMParser().parseFromString(rawHtml, 'text/html');
    // Shopify wraps section output in <div id="shopify-section-{name}">;
    // querySelector finds our <div data-cart-count> regardless of nesting depth.
    const wrapper = doc.body.querySelector('[data-cart-count]');
    if (!wrapper) return;

    const count = parseInt(wrapper.dataset.cartCount || '0', 10);
    const total = parseInt(wrapper.dataset.cartTotal || '0', 10);

    updateCartCount(count);
    const countEl = $('#CartDrawerCount');
    if (countEl) countEl.textContent = `(${count})`;
    const subtotalEl = $('#CartDrawerSubtotal');
    if (subtotalEl) subtotalEl.textContent = formatMoney(total);

    const body = $('#CartDrawerBody');
    if (body) body.innerHTML = wrapper.innerHTML;

    // Let other components know the cart updated and whether tablets are still present
    const hasTablets = !!wrapper.querySelector('a[href*="tablet"]');
    document.dispatchEvent(new CustomEvent('cart:refreshed', { detail: { count, hasTablets } }));
  },
};

/* ── Product Form — Add to Cart ─────────────────────────────── */
function initProductForms() {
  $$('[data-type="add-to-cart-form"]').forEach(form => {
    const wrapper = form.closest('.product-form-wrapper');
    if (!wrapper) return;
    const sectionId = wrapper.dataset.sectionId;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      const submitBtn = form.querySelector('[data-atc-btn]');
      const submitText = form.querySelector('.product-form__submit-text');
      const submitLoading = form.querySelector('.product-form__submit-loading');
      const message = $(`#atc-message-${sectionId}`);

      const variantId = parseInt(form.querySelector('[data-variant-id]')?.value, 10);
      const quantity = parseInt(form.querySelector('[name="quantity"]')?.value || 1, 10);
      // Read selling plan from any matching input — Seal Subscriptions may write to
      // input[name="selling_plan"] or input#selling_plan; pick the first non-empty value.
      let sellingPlan = null;
      form.querySelectorAll('input[name="selling_plan"], select[name="selling_plan"]').forEach(el => {
        if (el.value && !sellingPlan) sellingPlan = parseInt(el.value, 10);
      });

      if (!variantId) return;

      // Tablet add-on (modal-selected)
      const tabletCheck  = wrapper.querySelector('[data-tablet-addon-check]');
      const isTabletSel  = tabletCheck && tabletCheck.dataset.tabletSelected === 'true';
      const tabletVid    = tabletCheck && tabletCheck.dataset.tabletVariant ? parseInt(tabletCheck.dataset.tabletVariant, 10) : 0;
      const tabletPlanId = tabletCheck && tabletCheck.dataset.tabletPlan ? parseInt(tabletCheck.dataset.tabletPlan, 10) : null;

      // Loading state
      submitBtn.disabled = true;
      if (submitText) submitText.hidden = true;
      if (submitLoading) submitLoading.hidden = false;
      if (message) { message.textContent = ''; message.className = 'product-form__message'; }

      const items = [{ id: variantId, quantity }];
      if (sellingPlan) items[0].selling_plan = sellingPlan;
      // Tablet is added directly by the modal; do not re-add here

      try {
        const cart = await Cart.add(items);
        updateCartCount(cart.items ? cart.items.reduce((t, i) => t + i.quantity, 0) : 0);
        await CartDrawer.refresh();

        if (message) {
          message.textContent = 'Added to cart!';
          message.className = 'product-form__message product-form__message--success';
        }

        // Show tablet upsell popup if tablet not added via modal
        if (tabletCheck && !isTabletSel && tabletVid) {
          TabletUpsell.open();
        } else {
          CartDrawer.open();
        }
      } catch (err) {
        if (message) {
          message.textContent = err.message || 'Something went wrong. Please try again.';
          message.className = 'product-form__message product-form__message--error';
        }
      } finally {
        submitBtn.disabled = false;
        if (submitText) submitText.hidden = false;
        if (submitLoading) submitLoading.hidden = true;
      }
    });
  });
}

/* ── Selling Plan Selector ──────────────────────────────────── */
function initSellingPlanSelector() {
  $$('.product-form-wrapper').forEach(wrapper => {
    const sectionId = wrapper.dataset.sectionId;
    const sellingPlanInput = wrapper.querySelector('[data-selling-plan-input]');
    if (!sellingPlanInput) return;
    // Mirror any external write (e.g. Seal Subscriptions) to keep our state in sync
    sellingPlanInput.addEventListener('change', () => { /* value already set externally */ });

    wrapper.addEventListener('change', (e) => {
      const radio = e.target.closest('[data-purchase-type]');
      if (!radio) return;

      // Update selected state visually
      $$('.selling-plan-selector__option', wrapper).forEach(opt => opt.classList.remove('is-selected'));
      radio.closest('.selling-plan-selector__option')?.classList.add('is-selected');

      if (radio.value === 'subscription') {
        sellingPlanInput.value = radio.dataset.sellingPlanId || '';
      } else {
        sellingPlanInput.value = '';
      }
    });
  });
}

/* ── Variant Selector ───────────────────────────────────────── */
function initVariantSelector() {
  $$('.product-form-wrapper').forEach(wrapper => {
    const sectionId = wrapper.dataset.sectionId;
    const productDataEl = $(`#product-json-${sectionId}`);
    if (!productDataEl) return;

    let product;
    try { product = JSON.parse(productDataEl.textContent); } catch { return; }

    const selects = $$('[data-option-index]', wrapper);
    if (!selects.length) return;

    function getSelectedOptions() {
      return selects.map(s => s.value);
    }

    function findVariant(options) {
      return product.variants.find(v =>
        v.options.every((opt, i) => opt === options[i])
      );
    }

    function updateVariant() {
      const options = getSelectedOptions();
      const variant = findVariant(options);
      if (!variant) return;

      // Update hidden variant id input
      const variantInput = wrapper.querySelector('[data-variant-id]');
      if (variantInput) variantInput.value = variant.id;

      // Update price display
      const priceContainer = wrapper.closest('[data-section-id]')?.parentElement?.querySelector('[id^="ProductPrice"]') ||
                             wrapper.closest('.product-form-wrapper');
      if (priceContainer) {
        const currency = window.Shopify?.currency?.active || 'USD';
        const isoCode = window.Shopify?.currency?.active || 'USD';
        const hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
        let html = '';
        if (hasCompare) {
          html += `<span class="price price--sale" itemprop="price" content="${(variant.price/100).toFixed(2)}">${formatMoney(variant.price)}</span>`;
          html += `<span class="price price--compare"><s>${formatMoney(variant.compare_at_price)}</s></span>`;
          html += `<span class="price-badge">Save ${formatMoney(variant.compare_at_price - variant.price)}</span>`;
        } else {
          html += `<span class="price" itemprop="price" content="${(variant.price/100).toFixed(2)}">${formatMoney(variant.price)}</span>`;
        }
        html += `<meta itemprop="priceCurrency" content="${isoCode}">`;
        priceContainer.innerHTML = html;
      }

      // Update ATC button
      const atcBtn = wrapper.querySelector('[data-atc-btn]');
      if (atcBtn) {
        atcBtn.disabled = !variant.available;
        const txtEl = atcBtn.querySelector('.product-form__submit-text');
        if (txtEl) txtEl.textContent = variant.available ? 'Add to cart' : 'Sold out';
      }

      // Update selling plan prices if applicable
      if (variant.selling_plan_allocations) {
        $$('[data-purchase-type="subscription"]', wrapper).forEach(radio => {
          const planId = parseInt(radio.dataset.sellingPlanId, 10);
          const alloc = variant.selling_plan_allocations.find(a => a.selling_plan_id === planId);
          if (alloc && radio.dataset.sellingPlanPrice !== undefined) {
            radio.dataset.sellingPlanPrice = formatMoney(alloc.price);
          }
        });
      }
    }

    selects.forEach(s => s.addEventListener('change', updateVariant));
  });
}

/* ── Product Gallery Thumbnails ─────────────────────────────── */
function initGallery() {
  // Use the outer wrapper so we can find both .product-gallery slides
  // AND .product-gallery__thumbs (which is a sibling, not a child of .product-gallery)
  $$('.product-main__gallery, .product-showcase__gallery').forEach(wrapper => {
    const slides = $$('[data-slide]', wrapper);
    const thumbs = $$('[data-thumb]', wrapper);
    if (!slides.length) return;

    let currentIdx = 0;

    function goTo(idx) {
      currentIdx = Math.max(0, Math.min(idx, slides.length - 1));
      slides.forEach(s => s.classList.toggle('is-active', parseInt(s.dataset.slide, 10) === currentIdx));
      thumbs.forEach(t => t.classList.toggle('is-active', parseInt(t.dataset.thumb, 10) === currentIdx));
    }

    thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => goTo(parseInt(thumb.dataset.thumb, 10)));
    });

    // Touch swipe support for mobile
    const slideArea = slides[0]?.closest('.product-gallery') || slides[0]?.parentElement;
    if (slideArea) {
      let startX = 0;
      slideArea.addEventListener('touchstart', e => { startX = e.touches[0].clientX; }, { passive: true });
      slideArea.addEventListener('touchend', e => {
        const dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) < 40) return;
        goTo(dx < 0 ? currentIdx + 1 : currentIdx - 1);
      }, { passive: true });
    }
  });
}

/* ── FAQ Accordion ──────────────────────────────────────────── */
function initFAQ() {
  $$('.faq__question').forEach(btn => {
    btn.addEventListener('click', () => {
      const expanded = btn.getAttribute('aria-expanded') === 'true';
      const answerId = btn.getAttribute('aria-controls');
      const answer = document.getElementById(answerId);

      if (!answer) return;

      btn.setAttribute('aria-expanded', String(!expanded));
      if (expanded) {
        answer.setAttribute('hidden', '');
      } else {
        answer.removeAttribute('hidden');
      }
    });
  });
}

/* ── Mobile Nav ─────────────────────────────────────────────── */
function initMobileNav() {
  const toggle = $('[aria-controls="MobileNav"]');
  const nav = $('#MobileNav');
  if (!toggle || !nav) return;

  toggle.addEventListener('click', () => {
    const open = nav.classList.toggle('is-open');
    toggle.setAttribute('aria-expanded', String(open));
    nav.setAttribute('aria-hidden', String(!open));
  });
}

/* ── Qty Controls (non-form, e.g. cart page) ────────────────── */
function initQtyControls() {
  document.addEventListener('click', (e) => {
    const dec = e.target.closest('[data-qty-decrease]');
    const inc = e.target.closest('[data-qty-increase]');
    if (!dec && !inc) return;

    const control = (dec || inc).closest('.qty-control');
    if (!control) return;
    const input = control.querySelector('[data-qty-input]');
    if (!input) return;

    const current = parseInt(input.value, 10) || 1;
    input.value = dec ? Math.max(1, current - 1) : current + 1;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });
}

/* ── Cart Page qty + remove ─────────────────────────────────── */
function initCartPage() {
  const form = $('#CartForm');
  if (!form) return;

  // Remove buttons
  form.addEventListener('click', async (e) => {
    const removeBtn = e.target.closest('[data-remove-item]');
    if (!removeBtn) return;
    e.preventDefault();
    const key = removeBtn.dataset.removeItem;
    await Cart.remove(key);
    window.location.reload();
  });

  // Qty inputs
  $$('[data-cart-item-key]', form).forEach(input => {
    input.addEventListener('change', debounce(async () => {
      const key = input.dataset.cartItemKey;
      const qty = parseInt(input.value, 10);
      if (isNaN(qty)) return;
      await Cart.change(key, qty);
      window.location.reload();
    }, 600));
  });
}

/* ── Product Card quick-add ─────────────────────────────────── */
function initProductCardATC() {
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('.product-card__atc, .pcard__atc, .bundle-card__atc');
    if (!btn) return;
    if (btn.classList.contains('pcard__select-options')) return;
    e.preventDefault();

    const variantId = parseInt(btn.dataset.variantId, 10);
    if (!variantId || btn.disabled) return;

    const origHTML = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" style="animation:spin 0.7s linear infinite"><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2.5" stroke-dasharray="40" stroke-dashoffset="10"/></svg> Adding…';

    try {
      const cart = await Cart.add([{ id: variantId, quantity: 1 }]);
      const total = cart.items ? cart.items.reduce((t, i) => t + i.quantity, 0) : 0;
      updateCartCount(total);
      await CartDrawer.refresh();
      CartDrawer.open();
      btn.innerHTML = '✓ Added!';
      setTimeout(() => { btn.innerHTML = origHTML; btn.disabled = false; }, 1800);

      // Show tablet upsell if product is not a tablet
      const productType = (btn.dataset.productType || btn.closest('.pcard')?.dataset.type || '').toLowerCase();
      const isTablet = productType.includes('tablet') || productType.includes('refill') || productType.includes('cleaning');
      if (!isTablet) {
        setTimeout(() => TabletUpsell.open(), 500);
      }
    } catch {
      btn.innerHTML = '✗ Error';
      setTimeout(() => { btn.innerHTML = origHTML; btn.disabled = false; }, 1800);
    }
  });
}

/* ── Tablet Upsell Popup ─────────────────────────────────────── */
const TabletUpsell = {
  el: null,
  _resetUI: null,
  init() {
    this.el = document.getElementById('ht-tablet-upsell-modal-global');
    if (!this.el) return;
    const el = this.el;

    const sizeBtns   = $$('[data-upsell-size-btn]', el);
    const planRadios = $$('[data-upsell-plan-radio]', el);
    const confirmBtn = document.getElementById('ht-upsell-confirm-btn');
    const qtyDisplay = el.querySelector('[data-upsell-qty]');
    const qtyDecBtn  = el.querySelector('[data-upsell-qty-decrease]');
    const qtyIncBtn  = el.querySelector('[data-upsell-qty-increase]');

    let selVariantId = sizeBtns.length ? sizeBtns[0].dataset.upsellSizeBtn : null;
    let selPlanId    = null;
    let curSizeBtn   = sizeBtns[0] || null;
    let upsellQty    = 1;

    function parsePlanPrices(str) {
      const r = {};
      if (!str) return r;
      str.split('|').forEach(e => { const [id, p] = e.split(':'); if (id && p) r[id] = parseInt(p, 10); });
      return r;
    }
    function fmt(cents) {
      return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    function updatePrices() {
      if (!curSizeBtn) return;
      const onePrice   = parseInt(curSizeBtn.dataset.oneTimePrice, 10);
      const planPrices = parsePlanPrices(curSizeBtn.dataset.planPrices);
      const onePriceEl = el.querySelector('[data-upsell-plan-price="one_time"]');
      if (onePriceEl) onePriceEl.textContent = fmt(onePrice);
      Object.entries(planPrices).forEach(([pid, price]) => {
        const pEl = el.querySelector(`[data-upsell-plan-price="${pid}"]`);
        if (pEl) pEl.textContent = fmt(price);
      });
    }
    function updateQtyDisplay() {
      if (qtyDisplay) qtyDisplay.textContent = upsellQty;
      if (qtyDecBtn) qtyDecBtn.disabled = upsellQty <= 1;
    }

    sizeBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        curSizeBtn   = btn;
        selVariantId = btn.dataset.upsellSizeBtn;
        sizeBtns.forEach(b => b.classList.toggle('is-selected', b === btn));
        updatePrices();
      });
    });

    planRadios.forEach(radio => {
      radio.addEventListener('change', () => {
        selPlanId = radio.dataset.upsellSellingPlanId || null;
        $$('[data-upsell-plan-option]', el).forEach(opt => {
          opt.classList.toggle('is-selected', opt.contains(radio));
        });
      });
    });

    if (qtyDecBtn) qtyDecBtn.addEventListener('click', () => { if (upsellQty > 1) { upsellQty--; updateQtyDisplay(); } });
    if (qtyIncBtn) qtyIncBtn.addEventListener('click', () => { upsellQty++; updateQtyDisplay(); });
    updateQtyDisplay();

    if (confirmBtn) {
      confirmBtn.addEventListener('click', async () => {
        const vid = parseInt(selVariantId, 10);
        if (!vid) return;
        confirmBtn.disabled = true;
        confirmBtn.textContent = 'Adding\u2026';
        try {
          const cartItems = [{ id: vid, quantity: upsellQty }];
          if (selPlanId) cartItems[0].selling_plan = parseInt(selPlanId, 10);
          const cart = await Cart.add(cartItems);
          const total = cart.items ? cart.items.reduce((t, i) => t + i.quantity, 0) : 0;
          updateCartCount(total);
          await CartDrawer.refresh();
          confirmBtn.textContent = '\u2713 Added!';
          setTimeout(() => { this.close(); CartDrawer.open(); }, 900);
        } catch {
          confirmBtn.disabled = false;
          confirmBtn.textContent = 'Yes! Add Tablets to My Order';
        }
      });
    }

    // Backdrop + keyboard close
    el.addEventListener('click', (e) => { if (e.target === el) this.close(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });

    const closeBtn = document.getElementById('ht-upsell-global-close');
    const skipBtn  = document.getElementById('ht-upsell-global-skip');
    if (closeBtn) closeBtn.addEventListener('click', () => this.close());
    if (skipBtn)  skipBtn.addEventListener('click',  () => { this.close(); CartDrawer.open(); });

    // Reset function called by open() each time the modal is shown
    this._resetUI = () => {
      upsellQty    = 1;
      curSizeBtn   = sizeBtns[0] || null;
      selVariantId = curSizeBtn ? curSizeBtn.dataset.upsellSizeBtn : null;
      updateQtyDisplay();
      sizeBtns.forEach((b, i) => b.classList.toggle('is-selected', i === 0));
      // Default to the first Subscribe & Save plan if one exists, else one-time
      let defIdx = 0;
      for (let i = 0; i < planRadios.length; i++) {
        if (planRadios[i].dataset.upsellSellingPlanId) { defIdx = i; break; }
      }
      selPlanId = planRadios[defIdx] ? (planRadios[defIdx].dataset.upsellSellingPlanId || null) : null;
      planRadios.forEach((r, i) => { r.checked = i === defIdx; });
      $$('[data-upsell-plan-option]', el).forEach((opt, i) => opt.classList.toggle('is-selected', i === defIdx));
      updatePrices();
    };
  },
  async open() {
    if (!this.el) this.init();
    if (!this.el) return;
    // Reset all selections and button state each time the modal opens
    if (this._resetUI) this._resetUI();
    const confirmBtn = document.getElementById('ht-upsell-confirm-btn');
    if (confirmBtn) { confirmBtn.disabled = false; confirmBtn.textContent = 'Yes! Add Tablets to My Order'; }
    // iOS Safari fix: window.innerHeight = visual viewport (excludes URL bar); 100vh includes it
    this.el.style.height = window.innerHeight + 'px';
    this.el.classList.add('ht-modal--open');
    document.body.style.overflow = 'hidden';
  },
  close() {
    if (!this.el) return;
    this.el.classList.remove('ht-modal--open');
    this.el.style.height = '';
    document.body.style.overflow = '';
  }
};

/* ── Collection sort ────────────────────────────────────────── */
function initCollectionSort() {
  const sortSelect = $('[data-sort-select]');
  if (!sortSelect) return;
  sortSelect.addEventListener('change', () => {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sortSelect.value);
    window.location.href = url.toString();
  });
}

/* ── Sticky header class ────────────────────────────────────── */
function initStickyHeader() {
  const header = $('#SiteHeader');
  if (!header) return;
  const announce = $('.announcement-bar');
  const announceH = announce ? announce.offsetHeight : 0;
  window.addEventListener('scroll', () => {
    header.classList.toggle('scrolled', window.scrollY > announceH + 10);
  }, { passive: true });
}

/* ── Format money helper ────────────────────────────────────── */
function formatMoney(cents) {
  const amount = (cents / 100).toFixed(2);
  const currency = window.Shopify?.currency?.active || 'USD';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);
  } catch {
    return `$${amount}`;
  }
}

function escapeHtml(str) {
  return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/* ── Related Products (product recommendations API) ─────────── */
function initRelatedProducts() {
  $$('[data-url]').forEach(async section => {
    const url = section.dataset.url;
    if (!url) return;
    try {
      const res = await fetch(url);
      const html = await res.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const inner = doc.querySelector('[data-url]');
      if (inner) section.innerHTML = inner.innerHTML;
    } catch { /* silent */ }
  });
}

/* ── Collection Filter Tabs (client-side, animated) ─────────── */
function initCollectionFilter() {
  const filterBar = document.querySelector('[data-collection-filters]');
  if (!filterBar) return;

  const tabs = [...filterBar.querySelectorAll('[data-filter]')];
  const grid = document.querySelector('.shop-product-grid');
  if (!grid) return;

  const cards = [...grid.querySelectorAll('.pcard')];
  const countEl = document.querySelector('.shop-grid-toolbar__count');
  const tabCount = filterBar.querySelector('[data-filter-count]');

  const handleize = (s) => s.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  // Normalize known singular/plural category drift so "Bundle" tag matches the
  // "bundles" tab, etc. Deterministic exact match only.
  const aliasMap = { bundle: 'bundles', tablet: 'tablets', accessory: 'accessories' };
  const norm = (s) => aliasMap[s] || s;

  // The Bundles tab shows the offer-cards deal section (not product cards — those
  // deals aren't real Shopify products). All other tabs show the product grid.
  const offersPanel = document.getElementById('shopify-section-collection-offers');
  const toolbar = document.querySelector('.shop-grid-toolbar');
  const gridSection = document.getElementById('shop-grid');
  const filterTabs = document.querySelector('.shop-filter-tabs');

  function applyFilter(filter) {
    const isBundles = filter === 'bundles';
    const filterN = norm(filter);

    if (offersPanel)  offersPanel.style.display       = isBundles ? 'block' : 'none';
    if (grid)         grid.style.display              = isBundles ? 'none' : '';
    if (toolbar)      toolbar.style.display           = isBundles ? 'none' : '';
    if (gridSection)  gridSection.style.paddingBottom = isBundles ? '0' : '';
    if (filterTabs)   filterTabs.style.marginBottom   = isBundles ? '0' : '';

    let visible = 0;
    if (!isBundles) {
      cards.forEach(card => {
        const type = norm(handleize(card.dataset.type || ''));
        const tagHandles = (card.dataset.tags || '').toLowerCase().split('|').map(handleize);
        const tagMatch = tagHandles.some(t => norm(t) === filterN);
        const match = filter === 'all' || type === filterN || tagMatch;
        card.style.display = match ? '' : 'none';
        if (match) visible++;
      });
      if (countEl) countEl.textContent = `${visible} product${visible !== 1 ? 's' : ''}`;
      if (tabCount) tabCount.textContent = cards.length;
    }

    tabs.forEach(t => {
      const isActive = t.dataset.filter === filter;
      t.classList.toggle('shop-filter-tab--active', isActive);
      t.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  tabs.forEach(tab => tab.addEventListener('click', () => applyFilter(tab.dataset.filter)));

  applyFilter('all');
}

/* ── Card entrance on scroll (IntersectionObserver) ─────────── */
function initCardEntrances() {
  const cards = document.querySelectorAll('.pcard');
  if (!cards.length || !('IntersectionObserver' in window)) return;

  const io = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('pcard--visible');
        io.unobserve(entry.target);
      }
    });
  }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });

  cards.forEach((card, i) => {
    card.style.setProperty('--card-i', i % 4);
    io.observe(card);
  });
}

/* ── Smooth scroll with header offset ───────────────────────── */
function initSmoothScroll() {
  document.addEventListener('click', (e) => {
    const link = e.target.closest('.js-scroll-to');
    if (!link) return;
    e.preventDefault();
    const targetId = link.dataset.target || link.getAttribute('href').replace('#', '');
    const target = document.getElementById(targetId);
    if (!target) return;
    const headerEl = document.querySelector('.site-header');
    const offset = headerEl ? headerEl.offsetHeight : 0;
    const top = target.getBoundingClientRect().top + window.scrollY - offset - 16;
    window.scrollTo({ top, behavior: 'smooth' });
  });
}

/* ── Tablet Add-On Modal ────────────────────────────────────── */
function initTabletAddonModal() {
  $$('.product-form-wrapper').forEach(wrapper => {
    const trigger = wrapper.querySelector('[data-tablet-addon-trigger]');
    const modal   = wrapper.querySelector('[data-tablet-modal]');
    if (!trigger || !modal) return;

    const check      = wrapper.querySelector('[data-tablet-addon-check]');
    const desc       = trigger.querySelector('[data-tablet-trigger-desc]');
    const icon       = trigger.querySelector('[data-tablet-trigger-icon]');
    const sizeBtns   = $$('[data-modal-size-btn]', modal);
    const planRadios = $$('[data-modal-plan-radio]', modal);
    const confirmBtn = modal.querySelector('[data-tablet-modal-confirm]');

    let selVariantId = sizeBtns.length ? sizeBtns[0].dataset.modalSizeBtn : (check ? check.dataset.tabletVariant : null);
    let selPlanId    = null;
    let curSizeBtn   = sizeBtns[0] || null;
    // Default selected plan = whichever radio is pre-checked in markup (Subscribe & Save)
    planRadios.forEach(r => { if (r.checked) selPlanId = r.dataset.modalSellingPlanId || null; });

    // Capture original icon/desc so we can restore them if the tablet is removed from cart
    const origIconHtml = icon ? icon.innerHTML : '';
    const origDescText = 'Get the deepest and best clean with our tablets. Choose size and save up to 10% with a subscription';

    function resetTrigger() {
      trigger.classList.remove('is-selected');
      if (icon) icon.innerHTML = origIconHtml;
      if (desc) desc.textContent = origDescText;
      if (check) { check.dataset.tabletSelected = ''; check.dataset.tabletPlan = ''; }
    }

    document.addEventListener('cart:refreshed', ({ detail }) => {
      if (!detail.hasTablets) resetTrigger();
    });

    // Quantity controls
    const qtyDisplay  = modal.querySelector('[data-tablet-modal-qty]');
    const qtyDecBtn   = modal.querySelector('[data-tablet-modal-qty-decrease]');
    const qtyIncBtn   = modal.querySelector('[data-tablet-modal-qty-increase]');
    let tabletQty = 1;
    function updateQtyDisplay() {
      if (qtyDisplay) qtyDisplay.textContent = tabletQty;
      if (qtyDecBtn) qtyDecBtn.disabled = tabletQty <= 1;
    }
    if (qtyDecBtn) qtyDecBtn.addEventListener('click', () => { if (tabletQty > 1) { tabletQty--; updateQtyDisplay(); } });
    if (qtyIncBtn) qtyIncBtn.addEventListener('click', () => { tabletQty++; updateQtyDisplay(); });
    updateQtyDisplay();

    function parsePlanPrices(str) {
      const r = {};
      if (!str) return r;
      str.split('|').forEach(e => { const [id, p] = e.split(':'); if (id && p) r[id] = parseInt(p, 10); });
      return r;
    }
    function fmt(cents) {
      return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    function updateModalPrices() {
      if (!curSizeBtn) return;
      const onePrice = parseInt(curSizeBtn.dataset.oneTimePrice, 10);
      const planPrices = parsePlanPrices(curSizeBtn.dataset.planPrices);
      const onePriceEl = modal.querySelector('[data-modal-plan-price="one_time"]');
      if (onePriceEl) onePriceEl.textContent = fmt(onePrice);
      Object.entries(planPrices).forEach(([pid, price]) => {
        const el = modal.querySelector(`[data-modal-plan-price="${pid}"]`);
        if (el) el.textContent = fmt(price);
      });
    }

    function openModal() { tabletQty = 1; updateQtyDisplay(); modal.hidden = false; document.body.style.overflow = 'hidden'; }
    function closeModal() { modal.hidden = true; document.body.style.overflow = ''; }

    sizeBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        curSizeBtn   = btn;
        selVariantId = btn.dataset.modalSizeBtn;
        sizeBtns.forEach(b => b.classList.toggle('is-selected', b === btn));
        updateModalPrices();
      });
    });

    planRadios.forEach(radio => {
      radio.addEventListener('change', () => {
        selPlanId = radio.dataset.modalSellingPlanId || null;
        $$('[data-modal-plan-option]', modal).forEach(opt => {
          opt.classList.toggle('is-selected', opt.contains(radio));
        });
      });
    });

    trigger.addEventListener('click', openModal);
    $$('[data-tablet-modal-close]', modal).forEach(el => el.addEventListener('click', closeModal));
    document.addEventListener('keydown', e => { if (e.key === 'Escape' && !modal.hidden) closeModal(); });

    if (confirmBtn) {
      confirmBtn.addEventListener('click', async () => {
        const vid = parseInt(selVariantId || (check ? check.dataset.tabletVariant : 0), 10);
        if (!vid) return;

        confirmBtn.disabled = true;
        confirmBtn.textContent = 'Adding\u2026';

        try {
          const cartItems = [{ id: vid, quantity: tabletQty }];
          if (selPlanId) cartItems[0].selling_plan = parseInt(selPlanId, 10);
          await Cart.add(cartItems);
          const freshCart = await Cart.get();
          updateCartCount(freshCart.items.reduce((t, i) => t + i.quantity, 0));

          if (check) {
            check.dataset.tabletSelected = 'true';
            check.dataset.tabletVariant  = String(vid);
            check.dataset.tabletPlan     = selPlanId || '';
          }
          trigger.classList.add('is-selected');
          if (icon) icon.innerHTML = '<svg width="22" height="22" viewBox="0 0 22 22" fill="none"><circle cx="11" cy="11" r="10" fill="#04b392"/><path d="M6.5 11l3.5 3.5 5.5-7" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
          const sizeLabel = curSizeBtn ? curSizeBtn.textContent.trim() : '';
          const planLabel = selPlanId ? 'Subscribe & Save' : 'One-time';
          if (desc) desc.textContent = (sizeLabel ? sizeLabel + ' \u00b7 ' : '') + planLabel + ' \u2014 in cart \u2713';
          closeModal();
          await CartDrawer.refresh();
          CartDrawer.open();
        } catch (err) {
          confirmBtn.textContent = 'Try again';
          confirmBtn.disabled = false;
          return;
        }

        confirmBtn.textContent = 'Add to My Order';
        confirmBtn.disabled = false;
      });
    }
  });
}

/* ── Init all ───────────────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  CartDrawer.init();
  initProductForms();
  initSellingPlanSelector();
  initVariantSelector();
  initGallery();
  initFAQ();
  initMobileNav();
  initQtyControls();
  initCartPage();
  initProductCardATC();
  initCollectionSort();
  initStickyHeader();
  initRelatedProducts();
  initCollectionFilter();
  initCardEntrances();
  TabletUpsell.init();
  initTabletAddonModal();
  initSmoothScroll();

  // Expose globals so inline section scripts can access them
  window.CartDrawer  = CartDrawer;
  window.TabletUpsell = TabletUpsell;
});
